import { useTheme } from "@mui/material";
import {
  MenuButtonAddTable,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonHorizontalRule,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectTextAlign,
  isTouchDevice,
} from "mui-tiptap";
import MenuButtonImagePicker from "./MenuButtonImagePicker";
import MenuButtonImageResizer from "./MenuButtonImageResizer";
import MenuButtonImageLink from "./MenuButtonImageLink";
import MenuButtonCollapseHeading from "./MenuButtonCollapseHeading";
import MenuButtonEditLinkCustom from "./MenuButtonEditLinkCustom";
import { useTranslation } from "react-i18next";
import MenuButtonHighlight from "./MenuButtonHighlight";
import MenuButtonTableStyles from "./MenuButtonTableStyles";
import { Tool } from "../../models/Tool";
import MenuSelectTextFormat from "./MenuSelectTextFormat";

type EditorMenuControlsProps = {
  onLinkToolInsert?: (tool: Tool) => void;
  onLinkRemove?: (toolId: string) => void;
  selectedTools?: Tool[];
};

export default function EditorMenuControls({
  onLinkToolInsert,
  onLinkRemove,
  selectedTools,
}: EditorMenuControlsProps = {}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <MenuControlsContainer>
      <MenuSelectTextFormat />
      <MenuButtonCollapseHeading
        defaultopen="true"
        tooltipLabel={t("collapseHeadingTooltipLabelDefaultOpen")}
        aria-label={t("collapseHeadingTooltipLabelDefaultOpen")}
      />
      <MenuButtonCollapseHeading
        tooltipLabel={t("collapseHeadingTooltipLabelDefaultClosed")}
        aria-label={t("collapseHeadingTooltipLabelDefaultClosed")}
      />
      <MenuDivider />
      <MenuButtonBold tooltipLabel={t("bold")} aria-label={t("bold")} />
      <MenuButtonItalic tooltipLabel={t("italic")} aria-label={t("bold")} />
      <MenuButtonUnderline
        tooltipLabel={t("underline")}
        aria-label={t("underline")}
      />
      <MenuButtonStrikethrough
        tooltipLabel={t("strikethrough")}
        aria-label={t("strikethrough")}
      />
      <MenuButtonSubscript
        tooltipLabel={t("subscript")}
        aria-label={t("subscript")}
      />
      <MenuButtonSuperscript
        tooltipLabel={t("superscript")}
        aria-label={t("superscript")}
      />
      <MenuDivider />
      <MenuButtonTextColor
        tooltipLabel={t("textColor")}
        aria-label={t("textColor")}
        defaultTextColor={theme.palette.text.primary}
        swatchColors={[
          { value: "#000000", label: "Black" },
          { value: "#ffffff", label: "White" },
          { value: "#888888", label: "Grey" },
          { value: "#ff0000", label: "Red" },
          { value: "#ff9900", label: "Orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#00d000", label: "Green" },
          { value: "#0000ff", label: "Blue" },
        ]}
      />
      <MenuButtonHighlight />
      <MenuDivider />
      <MenuButtonEditLinkCustom
        tooltipLabel={t("link")}
        ariaLabel={t("link")}
        onLinkInsert={onLinkToolInsert}
        onLinkRemove={onLinkRemove}
        selectedTools={selectedTools}
      />
      <MenuDivider />
      <MenuSelectTextAlign tooltipTitle={t("align")} aria-label={t("align")} />
      <MenuDivider />
      <MenuButtonOrderedList
        tooltipLabel={t("orderedList")}
        aria-label={t("orderedList")}
      />
      <MenuButtonBulletedList
        tooltipLabel={t("bulletedList")}
        aria-label={t("orderedList")}
      />
      {isTouchDevice() && (
        <>
          <MenuButtonIndent
            tooltipLabel={t("indent")}
            aria-label={t("indent")}
          />
          <MenuButtonUnindent
            tooltipLabel={t("unindent")}
            aria-label={t("unindent")}
          />
        </>
      )}
      <MenuDivider />
      <MenuButtonBlockquote
        tooltipLabel={t("blockquote")}
        aria-label={t("blockquote")}
      />
      <MenuDivider />
      <MenuButtonImagePicker
        tooltipLabel={t("insertImage")}
        ariaLabel={t("insertImage")}
      />
      <MenuButtonImageResizer
        tooltipLabel={t("resizeImage")}
        ariaLabel={t("resizeImage")}
      />
      <MenuButtonImageLink
        tooltipLabel={t("addLinkToTextOrImage")}
        ariaLabel={t("addLinkToTextOrImage")}
        onLinkInsert={onLinkToolInsert}
        onLinkRemove={onLinkRemove}
        selectedTools={selectedTools}
      />
      <MenuDivider />
      <MenuButtonHorizontalRule
        tooltipLabel={t("insertHorizontalRule")}
        aria-label={t("insertHorizontalRule")}
      />
      <MenuButtonAddTable
        tooltipLabel={t("insertTable")}
        aria-label={t("insertTable")}
      />
      <MenuButtonTableStyles
        tooltipLabel={t("tableStyles", "Table Styles")}
        aria-label={t("tableStyles", "Table Styles")}
      />
      <MenuDivider />
      <MenuButtonRemoveFormatting
        tooltipLabel={t("removeInlineFormatting")}
        aria-label={t("removeInlineFormatting")}
      />
      <MenuDivider />
      <MenuButtonUndo tooltipLabel={t("undo")} aria-label={t("undo")} />
      <MenuButtonRedo tooltipLabel={t("redo")} aria-label={t("redo")} />
    </MenuControlsContainer>
  );
}
